import { useCallback, useEffect, useRef, useState } from "react";
import { hasUnsavedChanges } from "@/services/app-update/unsaved-changes";
import { getUpdateState, reportRemoteVersion } from "@/services/app-update/update-state";
import {
  captureBaselineBuildId,
  fetchRemoteVersion,
} from "@/services/app-update/version-marker";

/**
 * App-wide detector for a newer frontend deploy, mounted once in AppLayout.
 *
 * Checks /version.json when the tab becomes visible, when the window gains
 * focus, on a 20-minute backstop interval (visible tabs only), and on route
 * changes (at most once a minute). Results go to the shared update state,
 * which drives AppUpdateBanner.
 *
 * Applying the update: once an update is pending, the next route change does
 * a full reload of the new URL instead of staying on the old bundle, because
 * a route change is a natural pause. It is skipped while any mounted form
 * reports unsaved input (useUnsavedChanges): route-local forms are already
 * unmounted by then, so only input that survives navigation can block it.
 * The reload is claimed once per detected build in sessionStorage, so a host
 * that keeps serving an old index.html cannot cause a reload on every click.
 *
 * In Vite dev mode there is no `/assets/` entry script, the baseline is null,
 * and the hook does nothing.
 */
const POLL_INTERVAL_MS = 20 * 60 * 1000;
const NAVIGATION_CHECK_MIN_GAP_MS = 60 * 1000;
const NAVIGATION_RELOAD_KEY = "vardic:update-navigation-reload";

/** True when this tab has not yet reloaded for `target` on a navigation. */
function claimNavigationReload(target: string): boolean {
  try {
    if (sessionStorage.getItem(NAVIGATION_RELOAD_KEY) === target) return false;
    sessionStorage.setItem(NAVIGATION_RELOAD_KEY, target);
    return true;
  } catch {
    // Without storage there is no loop guard, so do not reload on our own;
    // the banner still offers the refresh.
    return false;
  }
}

export function useAppUpdateAvailable(pathname: string): void {
  const [baseline] = useState(captureBaselineBuildId);
  const abortRef = useRef<AbortController | null>(null);
  const lastCheckRef = useRef(0);
  const previousPathnameRef = useRef(pathname);

  const checkForUpdate = useCallback(async () => {
    if (baseline === null || document.visibilityState !== "visible") return;
    lastCheckRef.current = Date.now();
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const remote = await fetchRemoteVersion(controller.signal);
    if (remote === null || controller.signal.aborted) return;
    reportRemoteVersion(remote, baseline);
  }, [baseline]);

  useEffect(() => {
    if (baseline === null) return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") void checkForUpdate();
    };
    const onFocus = () => {
      void checkForUpdate();
    };
    const intervalId = setInterval(onFocus, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    globalThis.addEventListener("focus", onFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      globalThis.removeEventListener("focus", onFocus);
      abortRef.current?.abort();
    };
  }, [baseline, checkForUpdate]);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (baseline === null) return;

    const { status, remoteBuildId } = getUpdateState();
    if (
      status !== "current" &&
      !hasUnsavedChanges() &&
      claimNavigationReload(remoteBuildId ?? `required:${baseline}`)
    ) {
      globalThis.location.reload();
      return;
    }
    if (Date.now() - lastCheckRef.current >= NAVIGATION_CHECK_MIN_GAP_MS) {
      void checkForUpdate();
    }
  }, [pathname, baseline, checkForUpdate]);
}
